import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';


function BrainModel({ rotationProgress }) {
  const { scene } = useGLTF('/glb/Brain.glb');
  const brainRef = useRef();

  useFrame(() => {
    if (brainRef.current) {
      brainRef.current.rotation.y = rotationProgress * 2 * Math.PI;
    }
  });

  return <primitive ref={brainRef} object={scene} scale={2} />;
}

// Preload GLB outside the component
useGLTF.preload('/glb/Brain.glb');

export default function BrainScrollControl() {
  const brainWrapperRef = useRef();
  const [progress, setProgress] = useState(0);
  const [locked, setLocked] = useState(true);
  const [webglSupported, setWebglSupported] = useState(true);

  // Check WebGL support
  useEffect(() => {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      console.error('WebGL is not supported on this device.');
      setWebglSupported(false);
    }
  }, []);

  // Scroll progress logic
  useEffect(() => {
    const handleScroll = () => {
      const wrapper = brainWrapperRef.current;
      if (!wrapper || !locked) return;

      const rect = wrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.top <= 0 && rect.bottom > windowHeight) {
        const totalScroll = rect.height - windowHeight;
        const scrolled = -rect.top;
        const newProgress = Math.min(scrolled / totalScroll, 1);
        setProgress(newProgress);

        if (newProgress >= 1) {
          setLocked(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [locked]);

  return (
    <div>
      <div
        ref={brainWrapperRef}
        style={{
          height: '130vh',
          position: 'relative',
          display:'flex',
          justifyContent:"center",
          alignItems:'center'
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
          }}
        >
          {webglSupported ? (
            <Canvas
              gl={{ antialias: false, failIfMajorPerformanceCaveat: false }}
              camera={{ position: [0, 0, 8], fov: 50 }}
              onCreated={({ gl }) => {
                try {
                  const context = gl.getContext();
                  console.log("WebGL Context:", context ? "Created" : "Failed");
                } catch (e) {
                  console.error("WebGL context error:", e);
                }
              }}
            >
              <ambientLight intensity={0.6} />
              <directionalLight position={[5, 5, 5]} intensity={1} />
              <Suspense fallback={
                <mesh>
                  <boxGeometry args={[1, 1, 1]} />
                  <meshStandardMaterial color="gray" />
                </mesh>
              }>
                <BrainModel rotationProgress={progress} />
              </Suspense>
            </Canvas>
          ) : (
            <div
              style={{
                color: 'red',
                textAlign: 'center',
                paddingTop: '40vh',
                fontSize: '1.5rem',
              }}
            >
              WebGL is not supported on your browser or device.
            </div>
          )}
        </div>
      </div>
 
    </div>
  );
}
